import { cn } from "@/lib/utils";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/16/solid";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ComparisonMode } from "@/gql/graphql";
import dayjs from "dayjs";

type TrendIndicatorProps = {
  trend: number;
  currentValue?: number | null;
  previousValue?: number | null;
  startDate?: string;
  endDate?: string;
  comparisonMode?: ComparisonMode;
  formatValue?: (value: number) => string;
};

type DateRange = {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
};

const defaultNumberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
});

export function TrendIndicator({
  trend,
  currentValue,
  previousValue,
  startDate,
  endDate,
  comparisonMode,
  formatValue,
}: TrendIndicatorProps) {
  if (!isFinite(trend) || trend === 0) {
    return null;
  }

  const isPositive = trend >= 0;
  const formattedTrend = Math.abs(trend).toFixed(1);
  const resolvedPreviousValue = resolvePreviousValue(
    previousValue,
    currentValue,
    trend,
  );
  const periods =
    startDate && endDate && comparisonMode
      ? getComparisonPeriods(startDate, endDate, comparisonMode)
      : null;
  const formatStatValue =
    formatValue ?? ((value: number) => defaultNumberFormatter.format(value));
  const formattedCurrentValue =
    currentValue === null || currentValue === undefined
      ? null
      : formatStatValue(currentValue);
  const formattedPreviousValue =
    resolvedPreviousValue === null
      ? null
      : formatStatValue(resolvedPreviousValue);
  const currentPeriodLabel = periods ? formatDateRange(periods.current) : null;
  const previousPeriodLabel = periods
    ? formatDateRange(periods.previous)
    : null;
  const hasTooltipContent =
    formattedCurrentValue !== null &&
    currentPeriodLabel !== null &&
    previousPeriodLabel !== null;

  const indicator = (
    <div
      className={cn("flex items-center gap-1 text-sm font-medium", {
        "text-green-600": isPositive,
        "text-red-600": !isPositive,
      })}
    >
      {isPositive ? (
        <ArrowUpIcon className="h-4 w-4" />
      ) : (
        <ArrowDownIcon className="h-4 w-4" />
      )}
      <span>{formattedTrend}%</span>
    </div>
  );

  if (!hasTooltipContent) {
    return indicator;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{indicator}</TooltipTrigger>
      <TooltipContent className="flex w-[220px] flex-col gap-3 p-4 text-left">
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase text-gray-300">
            Current period
          </p>
          <p className="text-sm font-medium text-white">
            {formattedCurrentValue}
          </p>
          <p className="text-xs text-gray-400">{currentPeriodLabel}</p>
        </div>
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase text-gray-300">
            Previous period
          </p>
          <p className="text-sm font-medium text-white">
            {formattedPreviousValue ?? "—"}
          </p>
          <p className="text-xs text-gray-400">{previousPeriodLabel}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function resolvePreviousValue(
  explicitPreviousValue: number | null | undefined,
  currentValue: number | null | undefined,
  trend: number,
) {
  if (
    explicitPreviousValue !== null &&
    explicitPreviousValue !== undefined &&
    isFinite(explicitPreviousValue)
  ) {
    return explicitPreviousValue;
  }

  if (
    currentValue === null ||
    currentValue === undefined ||
    !isFinite(currentValue)
  ) {
    return null;
  }

  const ratio = 1 + trend / 100;
  if (!isFinite(ratio) || ratio <= 0) {
    return null;
  }

  return currentValue / ratio;
}

function getComparisonPeriods(
  start: string,
  end: string,
  comparisonMode: ComparisonMode,
): { current: DateRange; previous: DateRange } | null {
  const currentStart = dayjs(start);
  const currentEnd = dayjs(end);

  if (!currentStart.isValid() || !currentEnd.isValid()) {
    return null;
  }

  let previous: DateRange;

  if (comparisonMode === ComparisonMode.PreviousYear) {
    previous = {
      start: currentStart.subtract(1, "year"),
      end: currentEnd.subtract(1, "year"),
    };
  } else {
    const diffMs = Math.max(currentEnd.valueOf() - currentStart.valueOf(), 0);
    const previousEnd = currentStart.subtract(1, "millisecond");
    previous = {
      start: previousEnd.subtract(diffMs, "millisecond"),
      end: previousEnd,
    };
  }

  return {
    current: { start: currentStart, end: currentEnd },
    previous,
  };
}

function formatDateRange(range: DateRange) {
  const sameDay = range.start.isSame(range.end, "day");
  const sameYear = range.start.isSame(range.end, "year");

  if (sameDay) {
    return range.start.format("MMM D, YYYY");
  }

  if (sameYear) {
    return `${range.start.format("MMM D")} - ${range.end.format("MMM D, YYYY")}`;
  }

  return `${range.start.format("MMM D, YYYY")} - ${range.end.format("MMM D, YYYY")}`;
}
